import { Box, SectionHelper } from '@wix/design-system';
import classNames from 'classnames';
import { i18n } from 'i18next';
import { observer } from 'mobx-react';
import React, { createContext, useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { dataHooks } from '../../dataHooks';
import { EnterEmailStore } from '../../stores/enterEmail';
import { AppContextProvider } from '../AppLoader';
import { BodyContainer } from '../AuthPage/AuthPage';
import { DialogFormContainer } from '../DialogContainer';
import { DialogHeader } from '../DialogHeader/DialogHeader';
import { InputFieldWithLabel } from '../InputField';
import { SubmitButton } from '../SubmitButton';
import { ThemedText } from '../ThemedComponents';
import s from './EnterEmail.scss';

interface EnterEmailProps {
  title?: string;
  subTitle?: string;
  buttonContent?: string;
  dataHook?: string;
  backButtonCb?: Function;
  priority?: 'primary' | 'secondary';
}

export const EnterEmailContextProvider = createContext<{
  store?: EnterEmailStore;
}>({});

export const EnterEmail: React.FC<EnterEmailProps> = observer(
  ({
    title,
    subTitle,
    buttonContent,
    backButtonCb,
    dataHook = dataHooks.sso.enterEmailDialog.container,
    priority = 'secondary',
  }) => {
    const { store } = useContext(EnterEmailContextProvider);
    const {
      rootStore: {
        postSocialLoginEnterEmailStore,
        displayStore: { preset, secondaryDialogInputButtonGap },
      },
    } = useContext(AppContextProvider);
    const { t } = useTranslation();

    // Convert total visual gap to InputField marginBottom value using CSS calc()
    // InputField applies marginBottom as both margin AND padding (doubling the value)
    // WDS InputWithLabel has ~15px internal padding for status message placeholder
    // Formula: totalGap = 2 * marginBottom + 15 → marginBottom = (totalGap - 15) / 2
    // max() ensures values below 15px clamp to 0px margin (resulting in 15px minimum gap)
    const DEFAULT_MARGIN_BOTTOM = '6px'; // Results in 27px total gap (default)
    const inputMarginBottom = secondaryDialogInputButtonGap
      ? `max(0px, calc((${secondaryDialogInputButtonGap} - 15px) / 2))`
      : DEFAULT_MARGIN_BOTTOM;

    return (
      <Box direction="vertical" width="100%" align="center">
        <DialogFormContainer
          formClasses={[s.enterEmailContainer]}
          dataHook={dataHook}
          mainHeaderProps={{
            backButtonCallback: backButtonCb ?? store?.onBackButtonClicked,
          }}
        >
          <BodyContainer priority={priority}>
            <DialogHeader>
              <DialogHeader.Title flow="secondaryFlow">
                {title}
              </DialogHeader.Title>
              <DialogHeader.SubTitleSmall>
                {subTitle}
              </DialogHeader.SubTitleSmall>
            </DialogHeader>
            <Box direction="vertical" align="center">
              <InputFieldWithLabel
                className={classNames(s.emailInput, s.ssoEnterEmailInput)}
                required
                formField={store?.emailField}
                label={t('sso.enterEmailPage.input.title')}
                dataHook={dataHooks.sso.enterEmailDialog.emailInput}
                onEnterPressed={() => store?.submit()}
                type="email"
                autocomplete="email"
                marginBottom={inputMarginBottom}
              />
              <SubmitButton
                dataHook={dataHooks.sso.enterEmailDialog.submitButton}
                onClick={() => store?.submit()}
                skin={preset.login?.primaryButtonDesign?.skin}
              >
                {buttonContent ?? t('sso.enterEmailPage.submitButton.text')}
              </SubmitButton>
            </Box>
          </BodyContainer>
        </DialogFormContainer>
        {postSocialLoginEnterEmailStore?.showErrorMessageForFacebook2FaUsers &&
          postSocialLoginEnterEmailStore?.facebookWith2FaNotificationOpen && (
            <Box width={540} marginTop="large">
              <SectionHelper
                skin="danger"
                fullWidth={false}
                actionText={t(
                  'sso.enterEmailPage.facebookWithNo2FA.actionButton.text',
                )}
                showCloseButton={false}
                size="small"
                onAction={() =>
                  store?.goToLoginWithEmail(store.emailField.value)
                }
              >
                {t('sso.enterEmailPage.facebookWithNo2FA.content')}
              </SectionHelper>
            </Box>
          )}
      </Box>
    );
  },
);

const ErrorTemplate: React.FC<{
  i18nInstance: i18n;
  errorKey: string;
  errorCode?: string;
  linkCb?: () => void;
}> = ({ i18nInstance, errorKey = 'errors.general', errorCode, linkCb }) => {
  switch (errorKey) {
    case 'errors.email.sso.notAssociated':
      return (
        <Trans i18nKey={errorKey}>
          <ThemedText size="tiny" skin="error">
            You can't log in with SSO using this email address.
          </ThemedText>
        </Trans>
      );
    default:
      return (
        <ThemedText size="tiny" skin="error">
          {i18nInstance.t(errorKey, { errorCode })}
        </ThemedText>
      );
  }
};

export const getErrorTemplate = (
  i18nInstance: i18n,
  errorKey: string,
  errorCode?: string,
  linkCb?: () => void,
) => (
  <ErrorTemplate
    i18nInstance={i18nInstance}
    errorKey={errorKey}
    errorCode={errorCode}
    linkCb={linkCb}
  />
);
